import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldIcon, PlusIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { DataTable } from '../../components/ui/DataTable';
import { formatDate, formatPhone } from '../../lib/formatters';
import { MOCK_STAFF } from '../../lib/mockData';
import type { StaffAccount } from '../../lib/types';
const ROLE_LABELS: Record<string, string> = {
  owner: 'Chủ sở hữu',
  manager: 'Quản lý',
  cashier: 'Thu ngân',
  marketing: 'Marketing'
};
const PERMISSIONS = [{
  key: 'view_customers',
  label: 'Xem khách hàng',
  owner: true,
  manager: true,
  cashier: true,
  marketing: true
}, {
  key: 'edit_customers',
  label: 'Sửa khách hàng',
  owner: true,
  manager: true,
  cashier: false,
  marketing: false
}, {
  key: 'process_payments',
  label: 'Xử lý thanh toán',
  owner: true,
  manager: true,
  cashier: true,
  marketing: false
}, {
  key: 'manage_wallet',
  label: 'Quản lý ví',
  owner: true,
  manager: true,
  cashier: false,
  marketing: false
}, {
  key: 'adjust_points',
  label: 'Điều chỉnh điểm',
  owner: true,
  manager: true,
  cashier: false,
  marketing: false
}, {
  key: 'manage_promotions',
  label: 'Quản lý khuyến mãi',
  owner: true,
  manager: true,
  cashier: false,
  marketing: true
}, {
  key: 'view_reports',
  label: 'Xem báo cáo',
  owner: true,
  manager: true,
  cashier: false,
  marketing: true
}, {
  key: 'manage_staff',
  label: 'Quản lý nhân viên',
  owner: true,
  manager: false,
  cashier: false,
  marketing: false
}, {
  key: 'system_settings',
  label: 'Cài đặt hệ thống',
  owner: true,
  manager: false,
  cashier: false,
  marketing: false
}];
export function StaffRolesPage() {
  const columns = [{
    key: 'name',
    header: 'Nhân viên',
    render: (s: StaffAccount) => <div data-id="element-996">
          <p className="font-medium text-white" data-id="element-997">{s.name}</p>
          <p className="text-xs text-[#666666]" data-id="element-998">{s.email}</p>
        </div>
  }, {
    key: 'phone',
    header: 'SĐT',
    render: (s: StaffAccount) => <span className="text-[#B0B0B0]" data-id="element-999">{formatPhone(s.phone)}</span>
  }, {
    key: 'role',
    header: 'Vai trò',
    render: (s: StaffAccount) => <Badge variant="gold" size="sm" data-id="element-1000">
          {ROLE_LABELS[s.role]}
        </Badge>
  }, {
    key: 'status',
    header: 'Trạng thái',
    render: (s: StaffAccount) => <Badge variant={s.isActive ? 'success' : 'error'} size="sm" data-id="element-1001">
          {s.isActive ? 'Hoạt động' : 'Vô hiệu'}
        </Badge>
  }, {
    key: 'created',
    header: 'Ngày tạo',
    render: (s: StaffAccount) => <span className="text-xs text-[#666666]" data-id="element-1002">
          {formatDate(s.createdAt)}
        </span>
  }];
  return <div data-id="element-1003">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-1004">
        <div className="flex items-center justify-between mb-6" data-id="element-1005">
          <div data-id="element-1006">
            <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-id="element-1007">
              <ShieldIcon className="w-7 h-7 text-[#C8A84E]" data-id="element-1008" />
              Nhân Viên & Phân Quyền
            </h1>
            <p className="text-sm text-[#666666]" data-id="element-1009">
              {MOCK_STAFF.length} nhân viên
            </p>
          </div>
          <Button data-id="element-1010">
            <PlusIcon className="w-4 h-4" data-id="element-1011" />
            Thêm Nhân Viên
          </Button>
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.05
    }} className="mb-6" data-id="element-1012">
        <Card padding="none" data-id="element-1013">
          <DataTable columns={columns} data={MOCK_STAFF} keyExtractor={s => s.id} data-id="element-1014" />
        </Card>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} data-id="element-1015">
        <Card title="Ma Trận Phân Quyền" data-id="element-1016">
          <div className="overflow-x-auto -mx-5" data-id="element-1017">
            <table className="w-full min-w-[500px]" data-id="element-1018">
              <thead data-id="element-1019">
                <tr className="border-b border-[#2A2A2A]" data-id="element-1020">
                  <th className="text-left text-xs font-semibold text-[#666666] uppercase px-5 py-3" data-id="element-1021">
                    Quyền
                  </th>
                  <th className="text-center text-xs font-semibold text-[#666666] uppercase px-3 py-3" data-id="element-1022">
                    Chủ SH
                  </th>
                  <th className="text-center text-xs font-semibold text-[#666666] uppercase px-3 py-3" data-id="element-1023">
                    Quản lý
                  </th>
                  <th className="text-center text-xs font-semibold text-[#666666] uppercase px-3 py-3" data-id="element-1024">
                    Thu ngân
                  </th>
                  <th className="text-center text-xs font-semibold text-[#666666] uppercase px-3 py-3" data-id="element-1025">
                    Marketing
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1026">
                {PERMISSIONS.map(perm => <tr key={perm.key} className="border-b border-[#2A2A2A]/50" data-id="element-1027">
                    <td className="px-5 py-2.5 text-sm text-[#B0B0B0]" data-id="element-1028">
                      {perm.label}
                    </td>
                    {(['owner', 'manager', 'cashier', 'marketing'] as const).map(role => <td key={role} className="text-center px-3 py-2.5" data-id="element-1029">
                        {perm[role] ? <CheckCircleIcon className="w-4 h-4 text-[#22C55E] mx-auto" data-id="element-1030" /> : <XCircleIcon className="w-4 h-4 text-[#666666]/30 mx-auto" data-id="element-1031" />}
                      </td>)}
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card>
      </motion.div>
    </div>;
}